<template>
  <div class="payments-page">
    <header class="payments-page__header">
      <div>
        <h1>{{ $t('catalog.payments.title') }}</h1>
        <p>{{ $t('catalog.payments.subtitle') }}</p>
      </div>
      <button type="button" class="payments-page__btn" @click="openCreate">
        <i class="mdi mdi-plus"></i>
        {{ $t('catalog.payments.add') }}
      </button>
    </header>

    <div v-if="pagedTypes.length" class="payments-page__grid">
      <article
        v-for="type in pagedTypes"
        :key="type.id"
        class="payment-card"
        :class="{ 'payment-card--off': !isActive(type) }"
      >
        <div class="payment-card__top">
          <div class="payment-card__icon">
            <i class="mdi mdi-credit-card-outline"></i>
          </div>
          <div class="payment-card__actions">
            <button
              type="button"
              class="payment-card__icon-btn"
              :title="isActive(type) ? $t('catalog.payments.turnOff') : $t('catalog.payments.turnOn')"
              @click="toggleActive(type)"
            >
              <i :class="['mdi', isActive(type) ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline']"></i>
            </button>
            <button
              type="button"
              class="payment-card__icon-btn"
              :title="$t('catalog.common.edit')"
              @click="openEdit(type)"
            >
              <i class="mdi mdi-pencil-outline"></i>
            </button>
            <button
              type="button"
              class="payment-card__icon-btn payment-card__icon-btn--danger"
              :title="$t('catalog.common.delete')"
              @click="askDelete(type)"
            >
              <i class="mdi mdi-trash-can-outline"></i>
            </button>
          </div>
        </div>
        <h3>{{ type.label }}</h3>
        <div class="payment-card__meta">
          <span class="payment-card__code">{{ type.value }}</span>
          <span v-if="!isActive(type)" class="payment-card__status">{{ $t('catalog.payments.inactive') }}</span>
        </div>
      </article>
    </div>

    <div v-else class="payments-page__empty">
      <i class="mdi mdi-credit-card-plus-outline"></i>
      <p>{{ $t('catalog.payments.empty') }}</p>
      <span>{{ $t('catalog.payments.emptyHint') }}</span>
    </div>

    <div v-if="pageCount > 1" class="payments-page__pagination">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="5"
        color="#0b4f8a"
      />
    </div>

    <v-dialog v-model="formOpen" max-width="480">
      <v-card class="payments-page__dialog">
        <v-card-title>{{ isEdit ? $t('catalog.payments.editTitle') : $t('catalog.payments.createTitle') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="label" :label="$t('catalog.common.name')" outlined dense class="mb-2" />
          <v-text-field
            v-model="value"
            :label="$t('catalog.payments.code')"
            :hint="$t('catalog.payments.codeHint')"
            persistent-hint
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="formOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark :loading="saving" @click="save">{{ $t('catalog.common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteOpen" max-width="420">
      <v-card class="payments-page__dialog">
        <v-card-title>{{ $t('catalog.payments.deleteTitle') }}</v-card-title>
        <v-card-text>
          {{ $t('catalog.payments.deleteDescription', { name: deleteName }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">{{ $t('catalog.common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  createPaymentType,
  deletePaymentType,
  getPaymentTypes,
  updatePaymentType,
} from '@/api/paymentTypes'
import { notify, notifyError } from '@/utils/notify'

const PER_PAGE = 12

export default {
  name: 'PaymentTypeControl',
  data() {
    return {
      types: [],
      page: 1,
      formOpen: false,
      isEdit: false,
      typeId: null,
      label: '',
      value: '',
      saving: false,
      deleteOpen: false,
      deleteName: '',
      deleting: false,
    }
  },
  computed: {
    pageCount() {
      return Math.ceil(this.types.length / PER_PAGE) || 0
    },
    pagedTypes() {
      const start = (this.page - 1) * PER_PAGE
      return this.types.slice(start, start + PER_PAGE)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    isActive(type) {
      return type.is_active !== false
    },
    async load() {
      try {
        this.types = await getPaymentTypes()
        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
      } catch (error) {
        notifyError(error)
      }
    },
    openCreate() {
      this.isEdit = false
      this.typeId = null
      this.label = ''
      this.value = ''
      this.formOpen = true
    },
    openEdit(type) {
      this.isEdit = true
      this.typeId = type.id
      this.label = type.label
      this.value = type.value
      this.formOpen = true
    },
    askDelete(type) {
      this.typeId = type.id
      this.deleteName = type.label
      this.deleteOpen = true
    },
    async toggleActive(type) {
      try {
        await updatePaymentType(type.id, {
          label: type.label,
          value: type.value,
          is_active: !this.isActive(type),
        })
        notify(
          this.isActive(type)
            ? this.$t('catalog.payments.turnedOff')
            : this.$t('catalog.payments.turnedOn')
        )
        await this.load()
      } catch (error) {
        notifyError(error)
      }
    },
    async confirmDelete() {
      this.deleting = true
      try {
        await deletePaymentType(this.typeId)
        notify(this.$t('catalog.payments.deleted'), 'warning')
        this.deleteOpen = false
        await this.load()
      } catch (error) {
        notifyError(error, this.$t('catalog.payments.deleteFailed'))
      } finally {
        this.deleting = false
      }
    },
    async save() {
      if (!this.label) {
        notify(this.$t('catalog.categories.enterName'), 'warning')
        return
      }

      this.saving = true
      try {
        const payload = {
          label: this.label,
          value: this.value || null,
        }
        if (!this.isEdit) {
          payload.is_active = true
        }
        const result = this.isEdit
          ? await updatePaymentType(this.typeId, payload)
          : await createPaymentType(payload)

        notify(result.message || this.$t('catalog.common.saved'))
        this.formOpen = false
        await this.load()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.payments-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.payments-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.payments-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border: 0;
  border-radius: 12px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.2s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    background: #093f6d;
  }
}

.payments-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}

.payment-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  transition: border-color 0.2s ease, transform 0.15s ease, opacity 0.2s ease;

  &:hover {
    border-color: #b7c6d6;
    transform: translateY(-2px);
  }

  &--off {
    opacity: 0.55;
  }

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
  }
}

.payment-card__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.payment-card__code {
  font-size: 12px;
  color: #6b7a90;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.payment-card__status {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9aa8ba;
  background: #eef2f6;
  padding: 3px 8px;
  border-radius: 999px;
}

.payment-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.payment-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #e9f1f8;
  color: #0b4f8a;

  .mdi {
    font-size: 22px;
  }
}

.payment-card__actions {
  display: flex;
  gap: 4px;
}

.payment-card__icon-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #f3f6fa;
  color: #4a5d73;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s ease, color 0.15s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    background: #e4ebf3;
    color: #0b4f8a;
  }

  &--danger:hover {
    background: #fdeceb;
    color: #c62828;
  }
}

.payments-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 240px;
  padding: 40px 20px;
  border: 1px dashed #d0dae6;
  border-radius: 18px;
  color: #6b7a90;
  text-align: center;

  .mdi {
    font-size: 42px;
    color: #9eb0c3;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    font-size: 16px;
    font-weight: 650;
    color: #152033;
  }

  span {
    font-size: 13.5px;
  }
}

.payments-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.payments-page__dialog {
  border-radius: 16px !important;
}
</style>
